import { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import MarketingExpenseInfo from '@/components/Approval/@components/MarketingExpense';
import { getMarketingExpenseDraftSourceUrl } from '@/constant/approval';
import { useSnackbar } from '@/hooks/global-tools';
import { fetchApi } from '@/utils/api/customAxios';
import type { MarketingAnalysis } from '@/utils/approval/marketingExpense';
import type { ApprovalFormValues } from '../types';

type MarketingExpenseDraftSource = {
  data?: {
    display?: { subject?: string | null; source?: MarketingAnalysis | null };
  };
};

type MarketingExpenseFieldProps = {
  /** 작성 진입 URL 의 analysisId. 편집 모드는 저장 attributes.analysisId */
  entryAnalysisId?: string;
  isEditMode: boolean;
};

/** 마케팅 비용 품의(212) 본문 위 폼 — 분석 레코드를 매 진입마다 다시 조회(레거시 getData) */
const MarketingExpenseField = ({
  entryAnalysisId,
  isEditMode,
}: MarketingExpenseFieldProps) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { watch, setValue } = useFormContext<ApprovalFormValues>();
  const state = watch('marketingExpense');
  const analysisId = entryAnalysisId ?? state?.analysisId;
  const loadedRef = useRef<string | null>(null);
  // 알림이 snackbar 를 새로 만들어 effect 가 다시 도는 무한 반복 차단
  const missingNoticeShownRef = useRef(false);

  const showLoadError = useCallback(
    () =>
      snackbar.showError({
        message: t('마케팅 분석 결과를 불러오지 못했습니다.'),
      }),
    [snackbar, t],
  );

  useEffect(() => {
    if (isEditMode || entryAnalysisId || missingNoticeShownRef.current) return;
    missingNoticeShownRef.current = true;
    showLoadError();
  }, [isEditMode, entryAnalysisId, showLoadError]);

  useEffect(() => {
    if (!analysisId || loadedRef.current === analysisId) return;
    loadedRef.current = analysisId;
    fetchApi<MarketingExpenseDraftSource>({
      method: 'GET',
      url: getMarketingExpenseDraftSourceUrl(analysisId),
    })
      .then((res) => {
        const display = res?.data?.display;
        const analysis = display?.source;
        if (!analysis || typeof analysis !== 'object') throw new Error('empty');
        setValue('marketingExpense', { analysisId, analysis });
        // 편집은 저장 제목 유지(레거시는 편집 때도 분석 제목으로 덮어씀)
        const subject = display?.subject?.trim();
        if (subject && !isEditMode) setValue('subject', subject);
      })
      .catch(showLoadError);
  }, [analysisId, isEditMode, setValue, showLoadError]);

  return (
    <Box className="marketing-expense-form" sx={{ mb: '40px' }}>
      <MarketingExpenseInfo analysis={state?.analysis ?? null} />
    </Box>
  );
};

export default MarketingExpenseField;
